/* aa-collection.js — the collection page's behaviour (PLAN.md I1.12), and the search results
 * page's (I1.4a), which is the same toolbar, filter sheet and grid over `search` instead.
 *
 *   1. <aa-sheet>        the bottom sheet: sort, filters, and the quick filters from step 4
 *   2. load-more         reveals the next batch of cards already rendered on the page
 *   3. filter sheet      held choices, live counts, price range, large-facet views
 *   4. pinned bar        marks the sticky filter bar as stuck (hairline, header shadow off)
 *   5. sidebar height    keeps the desktop sidebar, and its apply button, inside the window
 *
 * Desktop (>= 990px, WIDE): the filter sheet is a sidebar (§3 `setLayout`) and the sort sheet opens
 * as a dropdown (§1, `data-aa-menu`). Both switch live when the window crosses the breakpoint.
 *
 * Nothing here formats money or builds a URL a link could carry instead: the sort options are real
 * links rendered by Liquid with the active filters in them, so they work before this file runs.
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. Bottom sheet
     ==========================================================================
     Modal: the page behind is locked and focus stays inside. The lock goes on the ROOT only, with
     the same class the menu drawer uses (`aa-drawer-locked`, M-208), so the sticky header keeps the
     viewport and the product page's sticky bar steps aside exactly as it does for the drawer.

     Closing — the close button, a tap on the overlay, Escape, or a downward drag on the header —
     never applies anything; a sheet that holds choices (the filters) discards them. */

  const WIDE = window.matchMedia('(min-width: 990px)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const DISMISS_RATIO = 0.25; // dragged past a quarter of its height, the sheet goes
  const FLICK_SPEED = 0.6; // or flicked down faster than this, in px per ms

  class AASheet extends HTMLElement {
    connectedCallback() {
      if (this.dataset.aaBound === 'true') return;
      this.dataset.aaBound = 'true';
      this.panel = this.querySelector('.aa-sheet__panel');
      this.lastFocus = null;

      this.addEventListener('click', (event) => {
        // A tap outside the panel closes, whether it lands on the overlay or on the sheet itself.
        if (event.target === this || event.target.closest('[data-aa-sheet-close]')) this.close();
      });
      // On the document, not the sheet: Escape must still close it when focus has fallen out of
      // the panel (a blurred field, a click on the overlay), and the handler ignores closed sheets.
      document.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('transitionend', (event) => {
        if (event.target === this.panel && !this.hasAttribute('data-open')) this.hidden = true;
      });
      // A dropdown is placed against its button, so it closes rather than drift when the page moves.
      const closeMenu = () => {
        if (this.isOpen && this.hasAttribute('data-menu')) this.close();
      };
      window.addEventListener('scroll', closeMenu, { passive: true });
      window.addEventListener('resize', closeMenu, { passive: true });
      this.bindDrag();
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.lastFocus = trigger || document.activeElement;
      // At desktop width a `data-aa-menu` sheet opens as a dropdown under its button: placed from
      // the button's box, no page lock, no drag. Decided per opening, so a resize between two
      // openings is honoured.
      const asMenu = this.hasAttribute('data-aa-menu') && WIDE.matches && this.lastFocus && this.lastFocus.getBoundingClientRect;
      this.toggleAttribute('data-menu', Boolean(asMenu));
      if (asMenu) {
        const box = this.lastFocus.getBoundingClientRect();
        this.style.setProperty('--aa-menu-top', Math.round(box.bottom + 8) + 'px');
        this.style.setProperty('--aa-menu-right', Math.round(document.documentElement.clientWidth - box.right) + 'px');
      }
      this.hidden = false;
      // Commit the closed position before opening, or the panel appears without travelling.
      void this.panel.offsetHeight;
      this.style.removeProperty('--aa-sheet-drag');
      this.setAttribute('data-open', '');
      if (!asMenu) document.documentElement.classList.add('aa-drawer-locked');
      if (this.lastFocus && this.lastFocus.setAttribute) this.lastFocus.setAttribute('aria-expanded', 'true');
      const first = this.panel.querySelector('[aria-current="true"]') || this.panel;
      first.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('aa-sheet:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('data-open');
      this.removeAttribute('data-dragging');
      this.style.removeProperty('--aa-sheet-drag');
      if (!this.hasAttribute('data-menu')) document.documentElement.classList.remove('aa-drawer-locked');
      if (this.lastFocus && this.lastFocus.setAttribute) this.lastFocus.setAttribute('aria-expanded', 'false');
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
      this.lastFocus = null;
      // With reduced motion there is no transition to wait for.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.hidden = true;
      this.dispatchEvent(new CustomEvent('aa-sheet:close', { bubbles: true }));
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.prototype.filter.call(
        this.panel.querySelectorAll(FOCUSABLE),
        (el) => el.offsetParent !== null
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* The header drags the sheet down. Pointer events cover touch, pen and mouse in one path; a
       press that starts on a button inside the header is left to the button. */
    bindDrag() {
      // Every view of a multi-view sheet (the filters) has its own header, and each one drags.
      this.querySelectorAll('[data-aa-sheet-drag]').forEach((handle) => this.bindDragOn(handle));
    }

    bindDragOn(handle) {
      let startY = 0;
      let lastY = 0;
      let lastT = 0;
      let speed = 0;
      let active = false;

      handle.addEventListener('pointerdown', (event) => {
        if (!this.isOpen || this.hasAttribute('data-menu') || event.button > 0 || event.target.closest('button, a, input')) return;
        active = true;
        startY = lastY = event.clientY;
        lastT = event.timeStamp;
        speed = 0;
        handle.setPointerCapture(event.pointerId);
        this.setAttribute('data-dragging', '');
      });

      handle.addEventListener('pointermove', (event) => {
        if (!active) return;
        const dy = Math.max(0, event.clientY - startY);
        const dt = event.timeStamp - lastT;
        if (dt > 0) speed = (event.clientY - lastY) / dt;
        lastY = event.clientY;
        lastT = event.timeStamp;
        this.style.setProperty('--aa-sheet-drag', dy + 'px');
      });

      const end = (event) => {
        if (!active) return;
        active = false;
        this.removeAttribute('data-dragging');
        const dy = Math.max(0, event.clientY - startY);
        if (dy > this.panel.offsetHeight * DISMISS_RATIO || speed > FLICK_SPEED) {
          this.close();
        } else {
          this.style.removeProperty('--aa-sheet-drag');
        }
      };
      handle.addEventListener('pointerup', end);
      handle.addEventListener('pointercancel', end);
    }
  }

  if (!customElements.get('aa-sheet')) customElements.define('aa-sheet', AASheet);

  // Openers are plain buttons naming their sheet, so a sheet can be opened from anywhere on the page.
  if (!window.AACollectionOpeners) {
    window.AACollectionOpeners = true;
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-aa-sheet-open]');
      if (!opener) return;
      const sheet = document.getElementById(opener.dataset.aaSheetOpen);
      if (sheet && typeof sheet.open === 'function') {
        event.preventDefault();
        sheet.open(opener);
      }
    });
  }

  /* ==========================================================================
     2. Load more
     ==========================================================================
     Every matching card is already in the grid, in-stock first (see sections/aa-collection.liquid);
     the ones past the first batch carry `hidden`. Each press reveals the next batch, updates the
     "showing X of Y" line from a template Liquid translated, and moves the progress bar. When the
     last card is out, the button goes; the status line stays as the count. */

  document.querySelectorAll('[data-aa-collection]').forEach((root) => {
    if (root.dataset.aaBound === 'true') return;
    root.dataset.aaBound = 'true';

    const button = root.querySelector('[data-aa-more-btn]');
    const grid = root.querySelector('[data-aa-grid]');
    if (!button || !grid) return;

    const perLoad = Number(root.dataset.perLoad) || 24;
    const total = Number(button.dataset.total) || 0;
    const shownEl = root.querySelector('[data-aa-shown]');
    const progress = root.querySelector('[data-aa-progress]');
    const template = shownEl ? shownEl.dataset.template : '';

    button.addEventListener('click', () => {
      const next = Array.prototype.slice.call(grid.querySelectorAll(':scope > .aa-card[hidden]'), 0, perLoad);
      next.forEach((card) => {
        card.hidden = false;
      });
      // The revealed cards' steppers and add buttons were rendered with the page, and aa-card.js
      // already wired them; re-running is safe (it skips anything marked bound).
      if (window.AACard && window.AACard.wireSteppers) window.AACard.wireSteppers(grid);
      if (window.AACard && window.AACard.wireQuickAdd) window.AACard.wireQuickAdd(grid);

      const shown = grid.querySelectorAll(':scope > .aa-card:not([hidden])').length;
      if (shownEl && template) shownEl.textContent = template.replace('__SHOWN__', String(shown));
      if (progress && total) progress.style.width = Math.min(100, (shown / total) * 100) + '%';
      if (grid.querySelector(':scope > .aa-card[hidden]') === null) button.hidden = true;

      // Keep the reader's place: focus lands on the first new card's title, not back at the top.
      const firstTitle = next[0] && next[0].querySelector('.aa-card__title');
      if (firstTitle) firstTitle.focus({ preventScroll: true });
    });
  });

  /* ==========================================================================
     3. Filter sheet
     ==========================================================================
     Every choice is a checkbox in one form (snippets/aa-collection-filters.liquid); the main view
     and the large-facet views are just different windows onto it. Nothing applies until the form
     is submitted ("Pokaż N produktów"), which navigates to the filtered URL, so a result is always
     a real, shareable page and Back works.

     While the buyer chooses, the button's count, every value's count and which values are disabled
     are kept live from sections/aa-collection-count.liquid. Closing the sheet restores what was there
     when it opened (decided 2026-10-01). */

  const COUNT_SECTION = 'aa-collection-count';
  const COUNT_DELAY = 250;
  const fold = (text) => String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  function readCount(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('script[data-aa-count]');
    if (node) {
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }
    return readPage(doc);
  }

  /* The fallback reads the same numbers off a full render of the filtered page: the sheet there
     already carries the pluralised button label, every value's count and the price bounds of its
     results. Heavier, so it is only used when the count section cannot be had — which is always
     the case under `shopify theme dev`, whose hot-reload layer answers 404 for a section that is
     not on the page (M-288), and would cover a failure of the Section Rendering API on the live
     store too. */
  function readPage(doc) {
    const sheet = doc.querySelector('[data-aa-filters]');
    if (!sheet) return null;
    const values = {};
    sheet.querySelectorAll('.aa-fs__check').forEach((input) => {
      const count = input.closest('[data-aa-option]').querySelector('[data-aa-value-count]');
      values[input.dataset.key] = Number(count && count.textContent) || 0;
    });
    const priceEl = sheet.querySelector('[data-aa-price]');
    const button = sheet.querySelector('[data-aa-show]');
    return {
      label: button ? button.textContent.trim() : '',
      values: values,
      min: priceEl ? Number(priceEl.dataset.boundMin) : 0,
      max: priceEl ? Number(priceEl.dataset.boundMax) : 0,
    };
  }

  /* Fetches the live numbers for a set of filter parameters: the count section first, the full page
     if that fails. Once the section has failed, the page is used straight away for the rest of the
     visit rather than paying for a 404 every time. */
  let sectionFailed = false;
  function fetchNumbers(baseUrl, query, signal, section) {
    const page = () =>
      fetch(baseUrl + (query.toString() ? '?' + query.toString() : ''), { signal: signal })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then(readCount);
    if (sectionFailed) return page();
    const withSection = new URLSearchParams(query);
    withSection.set('section_id', section || COUNT_SECTION);
    return fetch(baseUrl + '?' + withSection.toString(), { signal: signal }).then((response) => {
      if (response.ok) return response.text().then(readCount);
      sectionFailed = true;
      return page();
    });
  }

  // Quick-filter chips on the page open the filter sheet on one facet.
  if (!window.AACollectionQuick) {
    window.AACollectionQuick = true;
    document.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-aa-quick]');
      if (!chip) return;
      const sheet = document.getElementById(chip.dataset.aaQuickSheet);
      if (!sheet || typeof sheet.open !== 'function' || !sheet.aaFilters) return;
      event.preventDefault();
      sheet.open(chip);
      sheet.aaFilters.openQuick(chip.dataset.aaQuick);
    });
  }

  document.querySelectorAll('[data-aa-filters]').forEach((sheet) => {
    if (sheet.dataset.aaFiltersBound === 'true') return;
    sheet.dataset.aaFiltersBound = 'true';

    const form = sheet.querySelector('[data-aa-filter-form]');
    const main = form.querySelector('[data-aa-view="main"]');
    const showButton = main.querySelector('[data-aa-show]');
    const url = sheet.dataset.url;
    const sort = sheet.dataset.sort;
    // What every request carries besides the choices. Empty on a collection, whose URL is the
    // whole address; on the search page it is the query (snippets/aa-collection-filters.liquid).
    const fixed = sheet.dataset.params || '';
    const countSection = sheet.dataset.countSection || COUNT_SECTION;
    const closeIcon = (main.querySelector('[data-aa-sheet-close] svg') || document.createElement('span')).outerHTML;
    const checks = () => Array.prototype.slice.call(form.querySelectorAll('.aa-fs__check'));

    let snapshot = null;
    let baseline = null; // the applied state, for the sidebar's dirty check
    let applying = false;
    let request = null;
    let timer = 0;
    let boundsLoaded = false;

    /* ---- price ---------------------------------------------------------------- */

    const priceEl = form.querySelector('[data-aa-price]');
    const price = priceEl && {
      el: priceEl,
      minInput: priceEl.querySelector('[data-aa-price-min]'),
      maxInput: priceEl.querySelector('[data-aa-price-max]'),
      minRange: priceEl.querySelector('[data-aa-range-min]'),
      maxRange: priceEl.querySelector('[data-aa-range-max]'),
      range: priceEl.querySelector('[data-aa-range]'),
      paramMin: priceEl.dataset.paramMin,
      paramMax: priceEl.dataset.paramMax,
      unit: priceEl.dataset.unit || '',
      label: priceEl.dataset.label || '',
      boundMin: Number(priceEl.dataset.boundMin) || 0,
      boundMax: Number(priceEl.dataset.boundMax) || 0,
      // The range applied in the URL. The page's own bounds sit inside it, so the range inputs
      // clamp it on load; it is put back once the true bounds arrive (`loadBounds`).
      applied: priceEl.hasAttribute('data-applied-lo')
        ? { lo: Number(priceEl.dataset.appliedLo), hi: Number(priceEl.dataset.appliedHi) }
        : null,
    };

    function priceValues() {
      const lo = Number(price.minRange.value);
      const hi = Number(price.maxRange.value);
      return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
    }

    function paintRange() {
      const span = price.boundMax - price.boundMin || 1;
      const { lo, hi } = priceValues();
      price.range.style.setProperty('--lo', ((lo - price.boundMin) / span) * 100 + '%');
      price.range.style.setProperty('--hi', ((hi - price.boundMin) / span) * 100 + '%');
    }

    function setPrice(lo, hi) {
      const clampTo = (n) => Math.min(price.boundMax, Math.max(price.boundMin, Math.round(n)));
      lo = clampTo(lo);
      hi = clampTo(hi);
      if (lo > hi) [lo, hi] = [hi, lo];
      price.minRange.value = price.minInput.value = String(lo);
      price.maxRange.value = price.maxInput.value = String(hi);
      paintRange();
    }

    function setBounds(min, max) {
      if (!(max > min)) return;
      const { lo, hi } = priceValues();
      const wasFullLo = lo <= price.boundMin;
      const wasFullHi = hi >= price.boundMax;
      price.boundMin = min;
      price.boundMax = max;
      [price.minInput, price.maxInput, price.minRange, price.maxRange].forEach((input) => {
        input.min = String(min);
        input.max = String(max);
      });
      setPrice(wasFullLo ? min : lo, wasFullHi ? max : hi);
    }

    function priceChanged() {
      if (!price) return false;
      const { lo, hi } = priceValues();
      return lo > price.boundMin || hi < price.boundMax;
    }

    /* ---- state ---------------------------------------------------------------- */

    function params(includePrice) {
      const out = new URLSearchParams(fixed);
      checks().forEach((input) => {
        if (input.checked) out.append(input.name, input.value);
      });
      if (includePrice !== false && priceChanged()) {
        const { lo, hi } = priceValues();
        out.set(price.paramMin, String(lo));
        out.set(price.paramMax, String(hi));
      }
      return out;
    }

    function takeSnapshot() {
      return {
        checked: checks().map((input) => input.checked),
        price: price ? priceValues() : null,
      };
    }

    function restore(state) {
      checks().forEach((input, i) => {
        input.checked = Boolean(state.checked[i]);
      });
      if (price && state.price) setPrice(state.price.lo, state.price.hi);
      refresh();
    }

    /* ---- badges and chips ----------------------------------------------------- */

    const chosenIn = (facet) =>
      checks().filter((input) => input.checked && (!facet || input.name === facet));

    function updateBadges() {
      main.querySelectorAll('[data-aa-facet]').forEach((row) => {
        const badge = row.querySelector('[data-aa-badge]');
        if (!badge) return;
        const n = chosenIn(row.dataset.aaFacet).length;
        badge.textContent = String(n);
        badge.hidden = n === 0;
      });
    }

    function chipFor(text, onRemove) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'aa-fs__chip';
      const label = document.createElement('span');
      label.textContent = text;
      chip.appendChild(label);
      chip.insertAdjacentHTML('beforeend', closeIcon);
      const template = form.dataset.removeLabel || '';
      chip.setAttribute('aria-label', template ? template.replace('__LABEL__', text) : text);
      chip.addEventListener('click', onRemove);
      return chip;
    }

    function renderChips() {
      form.querySelectorAll('[data-aa-chips]').forEach((strip) => {
        const view = strip.closest('[data-aa-view]');
        const isMain = view === main;
        const isPrice = view.hasAttribute('data-aa-price-view');
        const facet = isMain || isPrice ? null : view.dataset.aaFacet;
        strip.textContent = '';
        // The main view lists every choice; a facet view only its own; the price view only price.
        (isPrice ? [] : chosenIn(facet)).forEach((input) => {
          const text = input.closest('[data-aa-option]').dataset.label;
          strip.appendChild(chipFor(text, () => {
            input.checked = false;
            refresh();
          }));
        });
        if ((isMain || isPrice) && priceChanged()) {
          const { lo, hi } = priceValues();
          strip.appendChild(chipFor(lo + '–' + hi + ' ' + price.unit, () => {
            setPrice(price.boundMin, price.boundMax);
            refresh();
          }));
        }
        strip.hidden = strip.children.length === 0;
      });
    }

    /* ---- live count ----------------------------------------------------------- */

    function fetchCount() {
      if (request) request.abort();
      request = new AbortController();
      showButton.setAttribute('aria-busy', 'true');
      fetchNumbers(url, params(), request.signal, countSection)
        .then((data) => {
          if (!data) return;
          showButton.textContent = data.label;
          form.querySelectorAll('[data-aa-option]').forEach((option) => {
            const input = option.querySelector('.aa-fs__check');
            const n = data.values[input.dataset.key];
            if (n === undefined) return;
            option.querySelector('[data-aa-value-count]').textContent = String(n);
            // Empty values grey out in place rather than vanish (owner's call, 2026-10-02).
            input.disabled = n === 0 && !input.checked;
          });
          form.querySelectorAll('[data-aa-search]').forEach(applySearch);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          // A failed count leaves the last good label; applying still works, so nothing is lost.
        })
        .finally(() => showButton.removeAttribute('aria-busy'));
    }

    function loadBounds() {
      if (boundsLoaded || !price || !price.el.hasAttribute('data-needs-bounds')) return Promise.resolve();
      boundsLoaded = true;
      // The page's own products are already inside the applied price range, so the true bounds
      // come from asking again without it.
      return fetchNumbers(url, params(false), new AbortController().signal, countSection)
        .then((data) => {
          if (!data) return;
          setBounds(data.min, data.max);
          if (price.applied) setPrice(price.applied.lo, price.applied.hi);
          renderChips();
        })
        .catch(() => {});
    }

    /* In the sidebar "Pokaż N produktów" is always there but inert until the held choices differ
       from what is applied (owner's call, 2026-10-05); in the sheet it always applies. */
    const stateKey = () => JSON.stringify(takeSnapshot());
    function updateDirty() {
      const dirty = baseline !== null && stateKey() !== baseline;
      sheet.toggleAttribute('data-dirty', WIDE.matches && dirty);
      showButton.disabled = WIDE.matches && !dirty;
    }

    function refresh() {
      updateBadges();
      renderChips();
      updateDirty();
      clearTimeout(timer);
      timer = setTimeout(fetchCount, COUNT_DELAY);
    }

    /* ---- views ------------------------------------------------------------------ */
    /* A view shows one facet. Large facets keep their list in their view; small facets keep theirs
       inline in the main view, and so does the price block. A quick filter (a chip on the page)
       shows that one facet as the whole sheet, so a small facet's list, or the price block, is
       MOVED into the view's slot and moved home again on close. Moving rather than copying keeps
       one checkbox per value, so the views can never disagree. */

    let opener = null;
    const viewFor = (id) => form.querySelector('[data-aa-view="' + id + '"]');

    const unitFor = (id) => (id === sheet.id + '-price' ? priceEl : form.querySelector('[data-aa-options="' + id + '"]'));

    function mount(id) {
      const slot = form.querySelector('[data-aa-slot="' + id + '"]');
      const unit = unitFor(id);
      if (slot && unit && unit.parentElement !== slot) slot.appendChild(unit);
    }

    function unmount(id) {
      const home = form.querySelector('[data-aa-home="' + id + '"]');
      const unit = unitFor(id);
      if (home && unit && unit.parentElement !== home) home.appendChild(unit);
    }

    function unmountAll() {
      form.querySelectorAll('[data-aa-home]').forEach((home) => unmount(home.dataset.aaHome));
    }

    function openView(id, from) {
      const view = viewFor(id);
      if (!view) return;
      opener = from;
      mount(id);
      const search = view.querySelector('[data-aa-search]');
      if (search) applySearch(search);
      view.hidden = false;
      void view.offsetWidth;
      view.setAttribute('data-active', '');
      // Once the sub-view covers it, the main view leaves the tab order entirely.
      setTimeout(() => {
        if (view.hasAttribute('data-active')) main.hidden = true;
      }, 260);
      const back = view.querySelector('[data-aa-back]');
      if (back) back.focus({ preventScroll: true });
    }

    function closeView(view, instant) {
      if (!view || view === main) return;
      main.hidden = false;
      view.removeAttribute('data-active');
      const search = view.querySelector('[data-aa-search]');
      setTimeout(() => {
        if (!view.hasAttribute('data-active')) view.hidden = true;
        view.classList.remove('is-instant');
        if (search && search.value) {
          search.value = '';
          applySearch(search);
        }
        // The list goes home once the view has slid away, not while it is still on screen.
        if (!view.hasAttribute('data-active')) unmount(view.dataset.aaView);
      }, instant ? 0 : 260);
      if (opener && !instant) opener.focus({ preventScroll: true });
      opener = null;
    }

    function closeAllViews() {
      form.querySelectorAll('.aa-fs__view--sub').forEach((view) => closeView(view, true));
      unmountAll();
      main.hidden = false;
      delete sheet.dataset.mode;
      form.querySelectorAll('[data-aa-toggle]').forEach((toggle) => setGroup(toggle, false));
    }

    // Quick filter: the facet's view IS the sheet — it appears without sliding, the back arrow is
    // hidden, and "Zastosuj" applies and closes (decided 2026-10-01).
    function openQuick(id) {
      const view = viewFor(id);
      if (!view) return;
      sheet.dataset.mode = 'quick';
      mount(id);
      view.classList.add('is-instant');
      view.hidden = false;
      view.setAttribute('data-active', '');
      main.hidden = true;
      const search = view.querySelector('[data-aa-search]');
      if (search) applySearch(search);
      // Focus stays on the sheet, not the search field (owner's call, 2026-10-02): on a phone a
      // focused field throws the keyboard up over the list the buyer came to see.
      renderChips();
    }

    sheet.aaFilters = { openQuick: openQuick };

    function setGroup(toggle, open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const group = toggle.closest('[data-aa-group]');
      if (group) group.classList.toggle('is-open', open);
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    }

    function applySearch(field) {
      // A facet view, or a large facet's panel in the sidebar.
      const view = field.closest('[data-aa-search-scope]');
      const query = fold(field.value.trim());
      let shown = 0;
      view.querySelectorAll('[data-aa-option]').forEach((option) => {
        // Search only narrows by name; a disabled (empty) value that matches still shows, greyed.
        const match = !query || fold(option.dataset.label).includes(query);
        option.hidden = !match;
        if (!option.hidden) shown += 1;
      });
      const none = view.querySelector('[data-aa-no-matches]');
      if (none) none.hidden = shown > 0;
    }

    /* ---- events ----------------------------------------------------------------- */

    form.addEventListener('click', (event) => {
      // In the sidebar every facet row, a large one included, folds its own panel.
      const toggle = event.target.closest('[data-aa-toggle]') || (WIDE.matches && event.target.closest('[data-aa-open-view]'));
      if (toggle) {
        const opening = toggle.getAttribute('aria-expanded') !== 'true';
        // One facet open at a time: on the phone (decided 2026-10-01) and in the sidebar, where the
        // open one fills the card (owner's call, 2026-10-05).
        const rows = WIDE.matches ? '[data-aa-group] > .aa-fs__row' : '[data-aa-toggle]';
        form.querySelectorAll(rows).forEach((other) => setGroup(other, false));
        setGroup(toggle, opening);
        return;
      }
      const viewOpener = event.target.closest('[data-aa-open-view]');
      if (viewOpener) {
        openView(viewOpener.dataset.aaOpenView, viewOpener);
        return;
      }
      const back = event.target.closest('[data-aa-back]');
      if (back) {
        closeView(back.closest('[data-aa-view]'));
        return;
      }
      if (event.target.closest('[data-aa-view-done]')) {
        // From the main sheet "Zastosuj" returns with the choices held; as a quick filter it applies.
        if (sheet.dataset.mode === 'quick') form.requestSubmit();
        else closeView(event.target.closest('[data-aa-view]'));
        return;
      }
      if (event.target.closest('[data-aa-clear]')) {
        checks().forEach((input) => {
          input.checked = false;
        });
        if (price) setPrice(price.boundMin, price.boundMax);
        refresh();
      }
    });

    form.addEventListener('change', (event) => {
      if (event.target.matches('.aa-fs__check')) refresh();
    });

    form.addEventListener('input', (event) => {
      const field = event.target;
      if (field.matches('[data-aa-search]')) {
        applySearch(field);
        return;
      }
      if (!price) return;
      if (field === price.minRange || field === price.maxRange) {
        // The thumbs may not cross; the one being dragged stops at the other.
        if (Number(price.minRange.value) > Number(price.maxRange.value)) {
          field.value = field === price.minRange ? price.maxRange.value : price.minRange.value;
        }
        price.minInput.value = price.minRange.value;
        price.maxInput.value = price.maxRange.value;
        paintRange();
        refresh();
      }
    });

    // Typed prices settle on change (blur or Enter), so a half-typed "3" on the way to "390" is
    // not snapped to the lower bound mid-keystroke.
    if (price) {
      [price.minInput, price.maxInput].forEach((input) => {
        input.addEventListener('change', () => {
          setPrice(Number(price.minInput.value) || price.boundMin, Number(price.maxInput.value) || price.boundMax);
          refresh();
        });
        input.addEventListener('keydown', (event) => {
          // Enter commits the typed price without submitting the form or dropping focus.
          if (event.key === 'Enter') {
            event.preventDefault();
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
      paintRange();
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      applying = true;
      const query = params();
      if (sort) query.set('sort_by', sort);
      const search = query.toString();
      window.location.href = url + (search ? '?' + search : '');
    });

    sheet.addEventListener('aa-sheet:open', () => {
      snapshot = takeSnapshot();
      loadBounds();
    });

    sheet.addEventListener('aa-sheet:close', () => {
      if (!applying && snapshot) restore(snapshot);
      snapshot = null;
      closeAllViews();
    });

    /* ---- layout: sheet or sidebar ---------------------------------------------- */
    /* At 990px and up the sheet is a sidebar (aa-collection.css): not a dialog, never "open",
       every group unfolded. The CSS already shows it before this runs, so nothing jumps. */

    function setLayout() {
      const wide = WIDE.matches;
      if (wide && sheet.isOpen) sheet.close();
      sheet.dataset.layout = wide ? 'sidebar' : 'sheet';
      if (wide) {
        sheet.hidden = false;
        form.removeAttribute('role');
        form.removeAttribute('aria-modal');
      } else {
        if (!sheet.isOpen) sheet.hidden = true;
        form.setAttribute('role', 'dialog');
        form.setAttribute('aria-modal', 'true');
      }
      form.querySelectorAll('[data-aa-group] > .aa-fs__row').forEach((row) => {
        if (wide) setGroup(row, row.parentElement.hasAttribute('data-aa-initial'));
        else if (row.hasAttribute('data-aa-open-view')) {
          row.removeAttribute('aria-expanded');
          const panel = document.getElementById(row.getAttribute('aria-controls'));
          if (panel) panel.hidden = true;
        } else setGroup(row, false);
      });
      form.querySelectorAll('.aa-fs__scope [data-aa-search]').forEach((field) => {
        if (!field.value) return;
        field.value = '';
        applySearch(field);
      });
      if (wide) ensureBaseline();
      updateDirty();
    }

    // The sidebar's baseline is what is applied. With a price filter that is only known once the
    // true bounds have put the applied range back, so it waits for them; the phone never needs it.
    function ensureBaseline() {
      if (baseline !== null) return;
      loadBounds().then(() => {
        if (baseline !== null) return;
        baseline = stateKey();
        updateDirty();
      });
    }

    setLayout();
    WIDE.addEventListener('change', setLayout);
    updateBadges();
    renderChips();
  });


  /* ==========================================================================
     4. Pinned filter bar
     ==========================================================================
     The bar is `position: sticky` under the header (aa-collection.css). CSS cannot tell when a sticky
     element is actually stuck, so this marks it: `is-stuck` on the bar draws its hairline, and
     `aa-coll-bar-stuck` on the root drops the header's shadow while both are pinned. Checked on
     scroll and resize, and again when the header's slide (and with it the bar's `top`) finishes. */

  document.querySelectorAll('.aa-coll__bar').forEach((bar) => {
    if (bar.dataset.aaStickBound === 'true') return;
    bar.dataset.aaStickBound = 'true';
    let queued = false;

    const check = () => {
      queued = false;
      const style = getComputedStyle(bar);
      const top = parseFloat(style.top) || 0;
      // At desktop width the bar is not sticky (the sidebar is), so it never counts as stuck.
      const stuck = style.position === 'sticky' && window.scrollY > 0 && bar.getBoundingClientRect().top <= top + 1;
      bar.classList.toggle('is-stuck', stuck);
      document.documentElement.classList.toggle('aa-coll-bar-stuck', stuck);
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(check);
    };

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    bar.addEventListener('transitionend', queue);
    check();
  });

  /* ==========================================================================
     5. Sidebar height
     ==========================================================================
     The sidebar is exactly as tall as the window allows (owner's call, 2026-10-05). Pinned, that is
     the window under the header (CSS). Before it pins it starts lower down the page, so until then
     it is sized to the room below its own top edge, growing as the page scrolls it into place,
     and its foot, the apply button, is never below the window. It ends FOOT_GAP above the window's
     foot, as it starts 32px under the header (CSS). */

  // Space left under the card, clear of the browser's link preview in the bottom-left corner.
  const FOOT_GAP = 32;

  document.querySelectorAll('[data-aa-filters]').forEach((sidebar) => {
    let queued = false;
    const fit = () => {
      queued = false;
      if (!WIDE.matches) {
        sidebar.style.removeProperty('--aa-fs-h');
        return;
      }
      // Never taller than it is pinned: at the end of the grid the card is pushed up the window,
      // and growing it then would run it over the description below.
      const pinnedTop = parseFloat(getComputedStyle(sidebar).top) || 0;
      const top = Math.max(sidebar.getBoundingClientRect().top, pinnedTop);
      sidebar.style.setProperty('--aa-fs-h', Math.max(320, Math.floor(window.innerHeight - top - FOOT_GAP)) + 'px');
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(fit);
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    WIDE.addEventListener('change', queue);
    sidebar.addEventListener('transitionend', queue);
    fit();
  });

})();
